/**
 * Feedback Triage — the page's four chips.
 *
 * Their colours and labels live in `triageTokens.ts`, which the view and the row also read; this
 * file is components only so fast refresh keeps working on them.
 */
import type { ReactNode } from 'react';
import { Bot } from 'lucide-react';
import type { FeedbackReport, OwnerId, PiaSummary } from '../../services/feedbackTriageApi';
import { OWNER_COLOR, PIA_CHIP, PRIORITY_CHIP, PRIORITY_LABEL, TYPE_CHIP } from './triageTokens';

export function Chip({ className, children }: { className: string; children: ReactNode }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-bold tracking-wide ${className}`}
    >
      {children}
    </span>
  );
}

export function PriorityChip({ priority }: { priority: FeedbackReport['priority'] }) {
  return <Chip className={PRIORITY_CHIP[priority]}>{PRIORITY_LABEL[priority]}</Chip>;
}

export function TypeChip({ type }: { type: FeedbackReport['type'] }) {
  return <Chip className={TYPE_CHIP[type]}>{type}</Chip>;
}

export function Avatar({ owner, initials }: { owner: OwnerId; initials: string }) {
  return (
    <span
      className={`flex size-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white ${OWNER_COLOR[owner]}`}
    >
      {initials}
    </span>
  );
}

/** Where Pia is with this report, linking to her PR when there is one. */
export function PiaChip({ pia }: { pia: PiaSummary }) {
  const text = (
    <>
      <Bot className="mr-1 size-3" aria-hidden />
      {pia.label}
      {pia.prNumber > 0 && ` · #${pia.prNumber}`}
    </>
  );
  if (pia.prUrl) {
    return (
      <a href={pia.prUrl} target="_blank" rel="noreferrer" className="inline-flex hover:underline" title={`Pia: ${pia.label}`}>
        <Chip className={PIA_CHIP[pia.phase]}>{text}</Chip>
      </a>
    );
  }
  return <Chip className={PIA_CHIP[pia.phase]}>{text}</Chip>;
}
